/**
 * RingsLive — 4 anéis ao vivo (Apple Watch style, paleta Keto Lab).
 *
 * Props:
 *   rings: { nutrition, training, hygiene, biomarkers } each with .pct
 *   size: pixel size of the SVG (default 220)
 *   onRingClick: optional callback (ringKey) — opens logger
 */

import React from "react";
import { motion } from "framer-motion";
import { Flame, Dumbbell, Droplet, Activity, ChevronRight } from "lucide-react";

const RING_META = {
  nutrition: { label: "Nutrição", hint: "Como comeste hoje?", cadence: "Diário", color: "#84cc16", Icon: Flame },
  training: { label: "Treino", hint: "Sessão de hoje", cadence: "Por sessão", color: "#a855f7", Icon: Dumbbell },
  hygiene: { label: "Higiene", hint: "Sono · água · sais", cadence: "Diário", color: "#06b6d4", Icon: Droplet },
  biomarkers: { label: "Biomarcadores", hint: "Peso e cintura", cadence: "Semanal", color: "#00ff88", Icon: Activity },
};

function Ring({ pct = 0, color, radius, stroke, offset }) {
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.min(100, pct) / 100) * circumference;
  return (
    <g transform={`translate(${offset}, ${offset})`}>
      <circle
        cx={radius + stroke / 2}
        cy={radius + stroke / 2}
        r={radius}
        fill="none"
        stroke={`${color}25`}
        strokeWidth={stroke}
      />
      <motion.circle
        cx={radius + stroke / 2}
        cy={radius + stroke / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        transform={`rotate(-90 ${radius + stroke / 2} ${radius + stroke / 2})`}
        initial={{ strokeDasharray: `0 ${circumference}` }}
        animate={{ strokeDasharray: `${dash} ${circumference}` }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        style={{ filter: `drop-shadow(0 0 6px ${color}80)` }}
      />
    </g>
  );
}

export default function RingsLive({ rings, size = 220, onRingClick }) {
  if (!rings) return null;
  const stroke = Math.round(size * 0.06);
  const gap = Math.round(stroke * 0.6);
  const order = ["nutrition", "training", "hygiene", "biomarkers"];

  // Build concentric radii
  const ringConfigs = order.map((key, i) => {
    const radius = (size / 2) - stroke / 2 - i * (stroke + gap);
    const offset = i * (stroke + gap);
    return { key, radius, offset, pct: rings[key]?.pct || 0 };
  });

  return (
    <div className="flex flex-col items-center" data-testid="rings-live">
      <svg width={size} height={size}>
        {ringConfigs.map((r) => (
          <Ring
            key={r.key}
            pct={r.pct}
            color={RING_META[r.key].color}
            radius={r.radius}
            stroke={stroke}
            offset={r.offset}
          />
        ))}
      </svg>
      {/* Legend — CTA-style tiles, obviously tappable */}
      <div className="grid grid-cols-2 gap-3 mt-5 w-full max-w-md">
        {order.map((key) => {
          const meta = RING_META[key];
          const Icon = meta.Icon;
          const pct = rings[key]?.pct || 0;
          const complete = pct >= 90;
          return (
            <motion.button
              key={key}
              onClick={() => onRingClick?.(key)}
              whileTap={{ scale: 0.96 }}
              whileHover={{ y: -2 }}
              className="group relative overflow-hidden text-left p-4 rounded-2xl border transition-all"
              style={{
                borderColor: `${meta.color}55`,
                background: `linear-gradient(140deg, ${meta.color}18 0%, rgba(9,9,11,0.95) 60%)`,
                boxShadow: `0 6px 22px ${meta.color}25, inset 0 1px 0 ${meta.color}20`,
              }}
              data-testid={`ring-legend-${key}`}
            >
              {/* Progress fill bottom */}
              <div
                className="absolute bottom-0 left-0 h-0.5 transition-all duration-700"
                style={{ width: `${Math.min(100, pct)}%`, background: meta.color, boxShadow: `0 0 8px ${meta.color}` }}
              />

              <div className="flex items-start gap-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `radial-gradient(circle, ${meta.color}40 0%, ${meta.color}10 70%)`,
                    boxShadow: `0 0 18px ${meta.color}55, inset 0 0 12px ${meta.color}30`,
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-300 leading-none">{meta.label}</p>
                    <span
                      className="text-[8px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded"
                      style={{ background: `${meta.color}20`, color: meta.color }}
                    >
                      {meta.cadence}
                    </span>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1 leading-tight">{meta.hint}</p>
                </div>
              </div>

              <div className="flex items-end justify-between mt-3">
                <p className="text-2xl font-black tabular-nums" style={{ color: meta.color }}>
                  {pct}<span className="text-sm opacity-70">%</span>
                </p>
                <div
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full transition-transform group-hover:translate-x-1"
                  style={{ color: meta.color, background: `${meta.color}15`, border: `1px solid ${meta.color}40` }}
                >
                  {complete ? "Ver" : "Registar"} <ChevronRight className="w-3 h-3" />
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
